import { useState, useCallback } from 'react';
import { getShippingRates as getShippingRatesAdmin } from '../api/api';
import { getShippingRates as getShippingRatesEmployee } from '../api/empApi';
import { handleShippingError } from '../utils/shippingUtils';
import { toast } from 'react-toastify';

/**
 * Hook to manage shipping rates - extracted from existing Ordersdetail.jsx logic
 * @param {string|null} initialServiceCode - Initial service code
 * @param {function} apiFunction - The API function to use (defaults to admin API)
 */
const useShipping = (initialServiceCode = null, apiFunction = null) => {
  const [selectedMethod, setSelectedMethod] = useState(initialServiceCode);
  const [loadingInfo, setLoadingInfo] = useState("");

  // Use provided API function or default to admin
  const getShippingRatesAPI = apiFunction || getShippingRatesAdmin;

  // Fetch shipping rates with delivery times and costs
  const calculateRate = useCallback(async (box, orderId) => {
    if (!box.length || !box.width || !box.height || !box.weight) {
      alert("Please fill in all fields for dimensions and weight.");
      return null;
    }

    const data = {
      length: box.length,
      width: box.width,
      height: box.height,
      weight: box.weight,
      id: orderId,
    };
    
    setLoadingInfo(orderId);
    try {
      const res = await getShippingRatesAPI(data);
      setLoadingInfo("");

      const serviceOrder = [
        "UPS Ground®",
        "UPS 2nd Day Air®",
        "UPS Next Day Air®",
      ];

      // Sort the shipping methods based on service_type
      // Each method includes: shipping_amount, service_code, service_type,
      // delivery_days, estimated_delivery_date, carrier_delivery_days
      const sortedShippingMethods = Array.isArray(res.data)
        ? [...res.data].sort(
            (a, b) =>
              serviceOrder.indexOf(a.service_type) -
              serviceOrder.indexOf(b.service_type)
          )
        : [];

      return sortedShippingMethods;
    } catch (error) {
      setLoadingInfo("");
      handleShippingError(error, toast, 'Failed to calculate shipping rates.');
      return null;
    }
  }, [getShippingRatesAPI]);

  // Exact logic from lines 783-785 of Ordersdetail.jsx
  const handleCheckboxChange = useCallback((service_code) => {
    setSelectedMethod(service_code);
  }, []);

  return {
    selectedMethod,
    loadingInfo,
    calculateRate,
    handleCheckboxChange,
    setSelectedMethod,
    setLoadingInfo,
  };
};

// Export the API functions for convenience
export { getShippingRatesAdmin, getShippingRatesEmployee };

export default useShipping;
